import React from 'react';
import {
  Autocomplete as MuiAutocomplete,
  FormControl,
  TextField,
  Box,
  Paper,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';

import theme from 'config/theme';

import styles from './autocomplete.style';

const filter = createFilterOptions();

const Autocomplete = ({
  value,
  onChange,
  type,
  loading = false,
  label = '',
  error,
  placeholder = 'Select',
  options,
  withAddOption = false,
  name,
  onInputChange,
  filterOptions,
  minWidth,
  autoCompleteHeight,
  disabled,
  ...props
}) => {
  const _handleOnChange = (_event, newValue) => {
    if (withAddOption && newValue?.isNew) {
      onChange(newValue.value);
      return;
    }
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleInputChange = (_event, newInputValue, reason) => {
    if (onInputChange) {
      onInputChange(newInputValue, reason);
    }
  };

  const renderPaper = ({ children }) => (
    <Paper sx={styles.paper} onMouseDown={(e) => e.preventDefault()}>
      {children}
    </Paper>
  );

  const renderOption = (autoCompleteProps, option) => {
    if (withAddOption) {
      return (
        <Box
          component="li"
          {...autoCompleteProps}
          key={option.value || 'key'}
          sx={styles.selectItem}
          dangerouslySetInnerHTML={{
            __html: option.text,
          }}
        />
      );
    }

    return (
      <Box
        component="li"
        {...autoCompleteProps}
        key={option.value || 'key'}
        sx={styles.selectItem}
      >
        {option.text || option.label || ''}
      </Box>
    );
  };

  const renderInput = (params) => {
    return (
      <TextField
        variant="outlined"
        label={label}
        error={!!error}
        sx={styles.textfield}
        placeholder={placeholder}
        data-testid={`input-${name || 'autocomplete'}`}
        {...params}
        onBlur={() => {
          if (withAddOption) {
            onChange(params.inputProps.value);
          }
        }}
      />
    );
  };

  return (
    <FormControl variant="outlined" sx={{ m: 0, width: '100%' }}>
      <Box sx={styles.wrapper(minWidth)}>
        <MuiAutocomplete
          sx={styles.autocomplete}
          disabled={disabled}
          value={value || null}
          onChange={_handleOnChange}
          onInputChange={handleInputChange}
          loading={loading}
          ListboxProps={{
            style: { maxHeight: autoCompleteHeight || 300 },
          }}
          getOptionDisabled={(option) => {
            return 'isActive' in option ? !option?.isActive : false;
          }}
          isOptionEqualToValue={(option, newValue) => {
            return option?.value === newValue?.value;
          }}
          getOptionLabel={(option) => {
            if (typeof option === 'string' || option instanceof String) {
              return option;
            }
            return option?.text || option?.label || '';
          }}
          options={options || []}
          PaperComponent={renderPaper}
          renderOption={renderOption}
          renderInput={renderInput}
          size="small"
          filterOptions={
            filterOptions ||
            ((opts, params) => {
              const filtered = filter(opts, params);

              if (!withAddOption) {
                return filtered;
              }

              const { inputValue } = params;
              // Suggest the creation of a new value
              const isExisting = opts.some(
                (option) => inputValue === option.text
              );
              if (inputValue !== '' && !isExisting) {
                filtered.push({
                  text: `Create <div style="background-color:${theme.palette.blues.primaryBlue100};color:${theme.palette.grays.gray600};border-radius: 6px; padding: 2px 6px;word-break: break-all">${inputValue}</div>`,
                  value: inputValue,
                  isNew: true,
                });
              }

              return filtered;
            })
          }
          {...props}
        />
      </Box>
    </FormControl>
  );
};

export default Autocomplete;
